import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import type { EmberChangeset } from 'ember-changeset';
import changeset from 'ember-changeset/helpers/changeset';
import add from 'ember-math-helpers/helpers/add';
import eq from 'ember-truth-helpers/helpers/eq';
import ModelList from 'parog-web/components/model-list';
import type FollowUpReason from 'parog-web/models/follow-up-reason';
import Dropdown from 'ui-kit/components/changeset/dropdown';
import DatePicker from '../../changeset/date-picker';

interface Signature {
  Args: {
    changeset: EmberChangeset;
    followUpReasonId: FollowUpReason['id'];
  };
}

export default class TreatmentHistoryEditionFormBreastCancerFormRadiotherapySection extends Component<Signature> {
  get #radiotherapies() {
    return this.args.changeset.get('radiotherapies');
  }

  onChangesetUpdate = (changeset: EmberChangeset) => {
    changeset.execute();
    this.#setAsDirty();
  };

  #setAsDirty() {
    this.args.changeset.set('radiotherapies', [].concat(this.#radiotherapies));
  }

  <template>
    <h2 class='ui dividing header'>Radiothérapie</h2>

    <div class='field'>
      <div class='field' id='radiotherapies' data-test-radiotherapy-list>
        <ModelList
          @model={{@changeset}}
          @propertyName='radiotherapies'
          @addButtonTitle='Ajouter une radiothérapie'
          as |Item|
        >
          {{#let (changeset Item.item) as |radiotherapyChangeset|}}
            <div
              class='ui segment'
              data-test-radiotherapy-list-item={{Item.index}}
            >
              <h3 class='ui dividing header'>Radiothérapie
                {{add Item.index 1}}</h3>
              <i
                class='trash icon absolute right-0 top-0 m-[0.5em] opacity-[0.5]'
                role='button'
                {{on 'click' Item.destroy}}
              >
              </i>
              <div>
                <div class='field'>
                  <DatePicker
                    @labelName='Consultation radiothérapeute'
                    @propertyName='consultation'
                    @changeset={{radiotherapyChangeset}}
                    @onChangesetUpdate={{this.onChangesetUpdate}}
                  />
                </div>
                <div class='field'>
                  <DatePicker
                    @labelName='Date de début'
                    @propertyName='startDate'
                    @changeset={{radiotherapyChangeset}}
                    @onChangesetUpdate={{this.onChangesetUpdate}}
                  />
                </div>
                <div class='field'>
                  <DatePicker
                    @labelName='Date de fin'
                    @propertyName='endDate'
                    @changeset={{radiotherapyChangeset}}
                    @onChangesetUpdate={{this.onChangesetUpdate}}
                  />
                </div>
                {{#if (eq @followUpReasonId 'onco-lung-cancer')}}
                  <div class='field'>
                    <Dropdown
                      @labelName='Résécabilité'
                      @propertyName='resectability'
                      @changeset={{radiotherapyChangeset}}
                      @onChangesetUpdate={{this.onChangesetUpdate}}
                      as |dropdown|
                    >
                      <dropdown.Item @key='pulmonary' @value='Pulmonaire' />
                      <dropdown.Item @key='cerebral' @value='Cérébrale' />
                      <dropdown.Item
                        @key='for-analgesic-purposes '
                        @value='À visée antalgique'
                      />
                    </Dropdown>
                  </div>
                {{else if (eq @followUpReasonId 'onco-pancreatic-cancer')}}
                  <div class='field'>
                    <Dropdown
                      @labelName='Type de radiothérapie'
                      @propertyName='radiotherapyType'
                      @changeset={{radiotherapyChangeset}}
                      @onChangesetUpdate={{this.onChangesetUpdate}}
                      as |dropdown|
                    >
                      <dropdown.Item
                        @key='external-radiotherapy'
                        @value='Radiothérapie externe'
                      />
                      <dropdown.Item
                        @key='metabolic-radiotherapy'
                        @value='Radiothérapie métabolique'
                      />
                      <dropdown.Item
                        @key='curietherapy'
                        @value='Curiéthérapie'
                      />
                    </Dropdown>
                  </div>
                {{/if}}
              </div>
            </div>
          {{/let}}
        </ModelList>
      </div>
    </div>
  </template>
}
